import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Sliders, Gift, Coins, Percent, Save, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNotifications } from '@/hooks/useNotifications';
import { useAdminConfig, useUpdateAdminConfig } from '@/hooks/useAdminConfig';

const configSchema = z.object({
  mstcToMsrRate: z.coerce
    .number()
    .min(0, 'Conversion rate must be 0 or greater')
    .max(1000, 'Conversion rate cannot exceed 1000'),
  signupBonusPoints: z.coerce
    .number()
    .int('Signup bonus must be a whole number')
    .min(0, 'Signup bonus must be 0 or greater')
    .max(5000, 'Signup bonus cannot exceed 5000'),
  msrRewardRate: z.coerce
    .number()
    .min(0, 'Reward rate must be 0 or greater')
    .max(1000, 'Reward rate cannot exceed 1000'),
});

type ConfigFormData = z.infer<typeof configSchema>;

export function PortalConfigPage() {
  const { success, error: showToastError } = useNotifications();
  const { data: config, isLoading, isError, refetch } = useAdminConfig();
  const { mutate: updateConfig, isPending: isUpdating } = useUpdateAdminConfig();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ConfigFormData>({
    resolver: zodResolver(configSchema),
    defaultValues: {
      mstcToMsrRate: 0,
      signupBonusPoints: 0,
      msrRewardRate: 0,
    },
  });

  // Sync form values when fetched config data arrives
  useEffect(() => {
    if (config) {
      reset({
        mstcToMsrRate: config.mstcToMsrRate,
        signupBonusPoints: config.signupBonusPoints,
        msrRewardRate: config.msrRewardRate,
      });
    }
  }, [config, reset]);

  const onSubmit = (data: ConfigFormData) => {
    updateConfig(data, {
      onSuccess: () => {
        success('Portal configuration updated successfully!');
      },
      onError: (err) => {
        const errMsg = err.response?.data?.message || 'Failed to update configuration';
        showToastError(errMsg);
      },
    });
  };

  const handleCancel = () => {
    if (config) {
      reset({
        mstcToMsrRate: config.mstcToMsrRate,
        signupBonusPoints: config.signupBonusPoints,
        msrRewardRate: config.msrRewardRate,
      });
      success('Form reset to saved configuration');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">Loading configuration...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center space-y-4 rounded-lg border border-dashed border-destructive/50 bg-destructive/5 p-8 text-center">
        <h3 className="text-lg font-semibold text-destructive">Failed to Load Configuration</h3>
        <p className="max-w-xs text-sm text-muted-foreground">
          There was an error fetching the system configurations. Please check your connection or
          retry.
        </p>
        <Button onClick={() => refetch()} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Portal Configuration</h1>
          <p className="text-muted-foreground">
            Manage application-wide rewards, bonus points, and exchange rates.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card className="overflow-hidden border-border bg-card shadow-lg">
          <CardHeader className="bg-muted/30 border-b border-border/50">
            <div className="flex items-center space-x-2">
              <Sliders className="h-5 w-5 text-primary" />
              <div>
                <CardTitle>System Settings</CardTitle>
                <CardDescription>
                  Adjust reward points, signup multipliers and conversion logic
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-3">
              {/* MSTC to MSR conversion rate */}
              <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm hover:border-primary/50 transition-colors">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400">
                  <Percent className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mstcToMsrRate" className="font-semibold text-sm">
                    MSTC to MSR Rate
                  </Label>
                  <p className="text-xs text-muted-foreground min-h-[32px]">
                    The conversion multiplier for transferring MSTC to MSR.
                  </p>
                </div>
                <Input
                  id="mstcToMsrRate"
                  type="number"
                  step="any"
                  disabled={isUpdating}
                  {...register('mstcToMsrRate')}
                  className={errors.mstcToMsrRate ? 'border-destructive' : ''}
                />
                {errors.mstcToMsrRate && (
                  <p className="text-xs text-destructive mt-1 font-medium">
                    {errors.mstcToMsrRate.message}
                  </p>
                )}
              </div>

              {/* Signup Bonus Points */}
              <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm hover:border-primary/50 transition-colors">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                  <Gift className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="signupBonusPoints" className="font-semibold text-sm">
                    Signup Bonus MSR
                  </Label>
                  <p className="text-xs text-muted-foreground min-h-[32px]">
                    MSR granted automatically to new users who sign up.
                  </p>
                </div>
                <Input
                  id="signupBonusPoints"
                  type="number"
                  disabled={isUpdating}
                  {...register('signupBonusPoints')}
                  className={errors.signupBonusPoints ? 'border-destructive' : ''}
                />
                {errors.signupBonusPoints && (
                  <p className="text-xs text-destructive mt-1 font-medium">
                    {errors.signupBonusPoints.message}
                  </p>
                )}
              </div>

              {/* MSR Reward Rate */}
              <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm hover:border-primary/50 transition-colors">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <Coins className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="msrRewardRate" className="font-semibold text-sm">
                    MSR Reward Rate
                  </Label>
                  <p className="text-xs text-muted-foreground min-h-[32px]">
                    The global multiplier rate for MSR point earnings.
                  </p>
                </div>
                <Input
                  id="msrRewardRate"
                  type="number"
                  step="any"
                  disabled={isUpdating}
                  {...register('msrRewardRate')}
                  className={errors.msrRewardRate ? 'border-destructive' : ''}
                />
                {errors.msrRewardRate && (
                  <p className="text-xs text-destructive mt-1 font-medium">
                    {errors.msrRewardRate.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isUpdating || !isDirty}
              >
                Reset Changes
              </Button>
              <Button
                type="submit"
                disabled={isUpdating || !isDirty}
                className="flex items-center gap-2"
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {isUpdating ? 'Saving Settings...' : 'Save Configuration'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
